/**
 * LiveRadioScreen.js
 *
 * A modern, lighter 'Coming Soon' UI for the Radio feature.
 * Focuses on communicating the value of radio for youth engagement
 * while the feature is under final development.
 */
import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  StatusBar,
  ScrollView,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const { width } = Dimensions.get('window');

const LiveRadioScreen = () => {
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();

  return (
    <View style={styles.container}>
      <StatusBar barStyle="dark-content" translucent backgroundColor="transparent" />
      <LinearGradient colors={['#F8FAFC', '#F1F5F9', '#E2E8F0']} style={styles.gradient} />
      <View style={[styles.header, { paddingTop: insets.top + 12 }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Ionicons name="arrow-back" size={20} color="#1E293B" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Radio</Text>
        <View style={{ width: 32 }} />
      </View>
      <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
        <View style={styles.heroSection}>
          <View style={styles.iconContainer}>
            <LinearGradient colors={['#6366F1', '#4F46E5']} style={styles.iconGradient}>
              <MaterialCommunityIcons name="radio-tower" size={40} color="#FFFFFF" />
            </LinearGradient>
            <View style={styles.pulseRing} />
          </View>
          <View style={styles.badge}>
            <Text style={styles.badgeText}>COMING SOON</Text>
          </View>
          <Text style={styles.mainTitle}>Amplify Your Voice</Text>
          <Text style={styles.subtitle}>The heartbeat of Youth Connect is about to get a whole lot louder.</Text>
        </View>
        <View style={styles.visionCard}>
          <View style={styles.visionItem}>
            <View style={styles.visionIcon}>
              <Ionicons name="people" size={20} color="#6366F1" />
            </View>
            <View style={styles.visionTextContainer}>
              <Text style={styles.visionLabel}>Youth Engagement</Text>
              <Text style={styles.visionDesc}>A unique intimate space for storytelling and real-time interaction.</Text>
            </View>
          </View>
          <View style={styles.visionItem}>
            <View style={styles.visionIcon}>
              <Ionicons name="musical-notes" size={20} color="#6366F1" />
            </View>
            <View style={styles.visionTextContainer}>
              <Text style={styles.visionLabel}>Inspiration & Vibes</Text>
              <Text style={styles.visionDesc}>Curated playlists and live shows to empower and uplift.</Text>
            </View>
          </View>
          <View style={styles.visionItem}>
            <View style={styles.visionIcon}>
              <Ionicons name="megaphone" size={20} color="#6366F1" />
            </View>
            <View style={styles.visionTextContainer}>
              <Text style={styles.visionLabel}>Your Platform</Text>
              <Text style={styles.visionDesc}>Your stage to share insights and spark meaningful change.</Text>
            </View>
          </View>
        </View>
        <View style={styles.footerInfo}>
          <Text style={styles.footerText}>Fine-tuning the frequency for you. Stay tuned!</Text>
          <TouchableOpacity style={styles.notifyButton} onPress={() => navigation.goBack()}>
            <Text style={styles.notifyButtonText}>Back to Dashboard</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  gradient: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    height: '100%',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingBottom: 10,
  },
  backButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '800',
    color: '#1E293B',
    letterSpacing: -0.5,
  },
  scrollContent: {
    paddingBottom: 20,
  },
  heroSection: {
    alignItems: 'center',
    paddingTop: 20,
    paddingHorizontal: 24,
  },
  iconContainer: {
    marginBottom: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconGradient: {
    width: 70,
    height: 70,
    borderRadius: 35,
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 6,
    shadowColor: '#6366F1',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    zIndex: 2,
  },
  pulseRing: {
    position: 'absolute',
    width: 85,
    height: 85,
    borderRadius: 42.5,
    borderWidth: 1.5,
    borderColor: '#6366F1',
    opacity: 0.1,
  },
  badge: {
    backgroundColor: 'rgba(99, 102, 241, 0.1)',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    marginBottom: 12,
  },
  badgeText: {
    color: '#6366F1',
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 1,
  },
  mainTitle: {
    fontSize: 24,
    fontWeight: '900',
    color: '#0F172A',
    textAlign: 'center',
    marginBottom: 8,
    letterSpacing: -0.5,
  },
  subtitle: {
    fontSize: 14,
    color: '#475569',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 20,
  },
  visionCard: {
    backgroundColor: '#FFFFFF',
    marginHorizontal: 20,
    borderRadius: 20,
    padding: 20,
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
  },
  visionItem: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  visionIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: '#F1F5F9',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  visionTextContainer: {
    flex: 1,
  },
  visionLabel: {
    fontSize: 14,
    fontWeight: '700',
    color: '#1E293B',
    marginBottom: 2,
  },
  visionDesc: {
    fontSize: 12,
    color: '#64748B',
    lineHeight: 18,
  },
  footerInfo: {
    marginTop: 20,
    alignItems: 'center',
    paddingHorizontal: 30,
  },
  footerText: {
    fontSize: 12,
    color: '#94A3B8',
    textAlign: 'center',
    fontStyle: 'italic',
    marginBottom: 20,
  },
  notifyButton: {
    backgroundColor: '#1E293B',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 25,
    elevation: 3,
  },
  notifyButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '700',
  },
});

export default LiveRadioScreen;
